import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  Building2,
  Clock,
  AlertTriangle,
  SearchCheck,
} from "lucide-react";
import { EventTimeline } from "./downtime-events-timeline";
import {
  calculateDowntimeHours,
  calculateRuntimePercentage,
  calculateMechanicalAvailabilityPercentage,
  filterDowntimeEventsByRange,
  getDowntimeReportingRange,
} from "@/lib/downtime-data-utils";
import { useNoDataStats } from "@/contexts/NoDataStatsContext.jsx";
import {
  formatAvailabilityPercentWhileLoading,
  resolveCommunicationsAvailabilityPercent,
} from "@/lib/no-data-utils.js";
import { steelChromeButton } from "@/lib/status-styles.js";
import { cn } from "@/lib/utils.js";

const PANEL_CARD_CLASS = "bg-card border border-border shadow-sm";

function DetailInfoCard({ icon: Icon, label, value }) {
  return (
    <Card className={PANEL_CARD_CLASS}>
      <CardContent className='p-4'>
        <div className='flex items-center gap-3'>
          <Icon className='w-5 h-5 text-muted-foreground' />
          <div>
            <p className='text-xs text-muted-foreground mb-1'>{label}</p>
            <p className='font-medium text-foreground'>{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SummaryStatBox({ label, value }) {
  return (
    <div className='min-w-[7rem] rounded border border-border px-4 py-3 text-center'>
      <p className='text-sm text-muted-foreground mb-1'>{label}</p>
      <p className='text-lg font-semibold text-foreground'>{value}</p>
    </div>
  );
}

function useCompressorDetailMetrics({
  open,
  compressor,
  selectedDateRange,
  customStartDate,
  customEndDate,
}) {
  const { getNoDataStatsForRange } = useNoDataStats();

  const reportingRange = useMemo(
    () =>
      getDowntimeReportingRange(
        selectedDateRange,
        customStartDate,
        customEndDate
      ),
    [selectedDateRange, customStartDate, customEndDate]
  );

  const statsCustomStart = customStartDate ?? "";
  const statsCustomEnd = customEndDate ?? "";

  const filteredEvents = useMemo(() => {
    if (!open || !compressor.events?.length) return [];

    if (selectedDateRange === "custom" && !reportingRange.hasCustomBounds) {
      return compressor.events;
    }

    return filterDowntimeEventsByRange(
      compressor.events,
      reportingRange.startDate,
      reportingRange.endDate
    );
  }, [
    open,
    compressor.events,
    selectedDateRange,
    reportingRange.hasCustomBounds,
    reportingRange.startDate,
    reportingRange.endDate,
  ]);

  const noDataStatsEntry = getNoDataStatsForRange(
    reportingRange.statsRange,
    statsCustomStart,
    statsCustomEnd
  );

  const { totalDTHours, mechanicalDTHours } =
    calculateDowntimeHours(filteredEvents);

  const runtimePercent = calculateRuntimePercentage(
    totalDTHours,
    reportingRange.startDate,
    reportingRange.endDate
  );
  const mechanicalAvailabilityPercent =
    calculateMechanicalAvailabilityPercentage(
      mechanicalDTHours,
      reportingRange.startDate,
      reportingRange.endDate
    );
  const communicationsAvailability = resolveCommunicationsAvailabilityPercent(
    noDataStatsEntry.data,
    compressor.assetId,
    compressor.communicationsAvailabilityPercent
  );

  return {
    filteredEvents,
    totalDTHours,
    summaryStats: [
      { label: "Runtime", value: `${runtimePercent.toFixed(1)}%` },
      {
        label: "Mechanical Availability",
        value: `${mechanicalAvailabilityPercent.toFixed(1)}%`,
      },
      {
        label: "Communication Availability",
        value: formatAvailabilityPercentWhileLoading(
          communicationsAvailability,
          noDataStatsEntry.isLoading
        ),
      },
    ],
  };
}

function buildDowntimeAnalysisUrl(compressor, filters) {
  const params = new URLSearchParams({ from: "downtime" });
  const {
    searchTerm,
    selectedDateRange,
    customStartDate,
    customEndDate,
    selectedLocation,
    selectedVendor,
  } = filters;

  if (searchTerm) params.set("search", searchTerm);
  if (selectedDateRange) params.set("dateRange", selectedDateRange);
  if (customStartDate) params.set("startDate", customStartDate);
  if (customEndDate) params.set("endDate", customEndDate);
  if (selectedLocation.length > 0) {
    params.set("locations", JSON.stringify(selectedLocation));
  }
  if (selectedVendor.length > 0) {
    params.set("vendors", JSON.stringify(selectedVendor));
  }

  return `/asset/${encodeURIComponent(String(compressor.assetId))}/downtime-analysis?${params.toString()}`;
}

function CompressorDetailDialogContent({
  open,
  onOpenChange,
  compressor,
  selectedDateRange,
  customStartDate,
  customEndDate,
  searchTerm,
  selectedLocation,
  selectedVendor,
}) {
  const { filteredEvents, totalDTHours, summaryStats } =
    useCompressorDetailMetrics({
      open,
      compressor,
      selectedDateRange,
      customStartDate,
      customEndDate,
    });

  const infoCards = [
    { icon: MapPin, label: "Location", value: compressor.assetLocation },
    { icon: MapPin, label: "Lease Name", value: compressor.assetLease },
    { icon: Building2, label: "Vendor", value: compressor.vendor },
    { icon: Clock, label: "Total Events", value: filteredEvents.length },
  ];

  const openDowntimeAnalysis = () => {
    window.open(
      buildDowntimeAnalysisUrl(compressor, {
        searchTerm,
        selectedDateRange,
        customStartDate,
        customEndDate,
        selectedLocation,
        selectedVendor,
      }),
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='max-w-4xl max-h-[95vh] flex flex-col overflow-hidden'
        data-testid='dialog-compressor-detail'>
        <DialogHeader className='flex-shrink-0'>
          <div className='flex items-center justify-between gap-3'>
            <div className='flex items-center gap-3'>
              <div className='p-2 bg-destructive/10 rounded-lg'>
                <AlertTriangle className='h-5 w-5 text-destructive' />
              </div>
              <div>
                <DialogTitle className='text-lg font-semibold text-foreground'>
                  Downtime Details - {compressor.assetNumber}
                </DialogTitle>
                <DialogDescription className='text-sm text-muted-foreground mt-1'>
                  {compressor.assetLocation} • {totalDTHours.toFixed(1)} h total
                  downtime
                </DialogDescription>
              </div>
            </div>
            <Button
              variant='outline'
              onClick={openDowntimeAnalysis}
              className={cn(steelChromeButton, "mt-4")}>
              <SearchCheck className='h-4 w-4 mr-1' />
              Downtime Analysis
            </Button>
          </div>
        </DialogHeader>

        <div className='flex flex-col space-y-6 overflow-y-auto flex-1 min-h-0'>
          <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
            {infoCards.map(({ icon, label, value }) => (
              <DetailInfoCard
                key={label}
                icon={icon}
                label={label}
                value={value}
              />
            ))}
          </div>

          <Card className={PANEL_CARD_CLASS}>
            <CardContent className='p-4'>
              <div className='flex items-center justify-between gap-4 flex-wrap'>
                <div>
                  <p className='text-sm text-muted-foreground mb-1'>
                    Total Downtime
                  </p>
                  <p className='text-2xl font-bold text-foreground'>
                    {totalDTHours.toFixed(1)} h
                  </p>
                </div>
                <div className='flex gap-4 flex-wrap'>
                  {summaryStats.map(({ label, value }) => (
                    <SummaryStatBox key={label} label={label} value={value} />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`${PANEL_CARD_CLASS} flex-1 flex flex-col min-h-0`}>
            <CardContent className='p-6 flex-1 flex flex-col min-h-0'>
              <EventTimeline events={filteredEvents} />
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CompressorDetailDialog(props) {
  if (!props.compressor) return null;
  return <CompressorDetailDialogContent {...props} />;
}
